import React, { useState } from "react";
import { usePolarisRecaptcha } from "../hooks/usePolarisRecaptcha";
import { Link } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/landing-page.css";
import "../styles/contact-page.css";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import { RecaptchaDisclosure } from "./RecaptchaDisclosure";

interface FormErrors {
    email?: string;
    message?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_MESSAGE_LENGTH = 20;

interface ContactPageProps {
    type?: "demo" | "sales";
}

export default function ContactPage({ type = "demo" }: ContactPageProps) {
    const apiEndpoint = type === "sales" ? "api/contact/sales-request" : "api/contact/demo-request";
    const recaptchaAction = type === "sales" ? "contact_sales" : "contact_demo";
    const { executeRecaptcha } = usePolarisRecaptcha();
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    function validate(): FormErrors {
        const errs: FormErrors = {};
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            errs.email = "Email address is required.";
        } else if (!EMAIL_REGEX.test(trimmedEmail)) {
            errs.email = "Please enter a valid email address.";
        }
        const trimmedMessage = message.trim();
        if (!trimmedMessage) {
            errs.message = "Message is required.";
        } else if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
            errs.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters.`;
        }
        return errs;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setServerError("");
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setSubmitting(true);
        try {
            const recaptchaToken = await executeRecaptcha(recaptchaAction);
            const res = await fetch(buildApiUrl(apiEndpoint), {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    email: email.trim(),
                    message: message.trim(),
                    recaptchaToken,
                }),
            });
            if (!res.ok) {
                // Never surface the raw response body to customers — map to friendly copy.
                setServerError(
                    res.status === 400 || res.status === 422
                        ? "Please enter a valid email and message, then try again."
                        : "We couldn't send your message right now. Please try again in a little while."
                );
                return;
            }
            setSubmitted(true);
        } catch (err) {
            const recaptchaFailed =
                err instanceof Error && err.message === "recaptcha-not-ready";
            setServerError(
                recaptchaFailed
                    ? "We couldn't verify this request. Please refresh the page and try again."
                    : "We couldn't send your message right now. Please try again in a little while."
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main className="contact-page landing-page">

            <PublicHeader />

            {/* Hero Banner */}
            <section className="contact-hero">
                <h1 className="contact-hero-heading">Contact Us</h1>
                <p className="contact-hero-breadcrumb">
                    <Link to="/">Home</Link>
                    <span className="contact-hero-breadcrumb-separator">&gt;</span>
                    Contact Us
                </p>
            </section>

            {/* Form + Info */}
            <section className="contact-content">
                <div className="contact-content-inner">

                    {/* Left: Form Card */}
                    <div className="contact-form-card">
                        {submitted ? (
                            <div className="contact-success">
                                <div className="contact-success-check">&#10003;</div>
                                <h3 className="contact-success-heading">Request Sent</h3>
                                <p className="contact-success-text">
                                    Thank you for your interest! We&apos;ve sent a confirmation to your email and will be in touch shortly.
                                </p>
                            </div>
                        ) : (
                            <>
                                <h2 className="contact-form-heading">Get In Touch</h2>
                                {serverError && <div className="contact-form-server-error">{serverError}</div>}
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="contact-form-group">
                                        <label className="contact-form-label">Your Email</label>
                                        <div className="contact-form-input-wrapper">
                                            <input
                                                type="email"
                                                className={`contact-form-input${errors.email ? " contact-form-input--error" : ""}`}
                                                placeholder="your email..."
                                                value={email}
                                                onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: undefined })); }}
                                            />
                                            <span className="contact-form-field-icon">&#9992;</span>
                                        </div>
                                        {errors.email && <p className="contact-form-error">{errors.email}</p>}
                                    </div>
                                    <div className="contact-form-group">
                                        <label className="contact-form-label">Message</label>
                                        <div className="contact-form-textarea-wrapper">
                                            <textarea
                                                className={`contact-form-textarea${errors.message ? " contact-form-textarea--error" : ""}`}
                                                placeholder="write message.."
                                                value={message}
                                                onChange={(e) => { setMessage(e.target.value); setErrors((prev) => ({ ...prev, message: undefined })); }}
                                            />
                                            <span className="contact-form-textarea-icon">&#9993;</span>
                                        </div>
                                        {errors.message && <p className="contact-form-error">{errors.message}</p>}
                                    </div>
                                    <button type="submit" className="contact-form-submit-btn" disabled={submitting}>
                                        {submitting ? "Sending..." : "Discover More"}
                                        {!submitting && <span className="contact-form-submit-arrow">&rarr;</span>}
                                    </button>
                                    <RecaptchaDisclosure className="recaptcha-disclosure--on-dark" />
                                </form>
                            </>
                        )}
                    </div>

                    {/* Right: Contact Info */}
                    <div className="contact-info">
                        <h2 className="contact-info-heading">Contact Us</h2>
                        <div className="contact-info-items">
                            <div className="contact-info-item">
                                <div className="contact-info-icon">&#128205;</div>
                                <div className="contact-info-text">
                                    <span className="contact-info-label">Location</span>
                                    <span className="contact-info-value">Austin, Texas</span>
                                </div>
                            </div>
                            <div className="contact-info-item">
                                <div className="contact-info-icon">&#9993;</div>
                                <div className="contact-info-text">
                                    <span className="contact-info-label">Email Address</span>
                                    <span className="contact-info-value">info@polarisathlete.com</span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            <PublicFooter />

        </main>
    );
}
